import { useTranslation } from 'react-i18next';
import { IconStar } from './Icons';

/**
 * Shared product-rating UI.
 *
 * Every rating surface in the app (product grid, Top Sales carousel, wishlist,
 * seller storefront, related/recommended strips) renders through these two
 * components, so the rating green exists in exactly one token
 * (--color-rating) and one set of CSS rules rather than being pasted into
 * each card variant.
 *
 * Green is a deliberate, narrow exception to the four-colour brand palette and
 * is approved for ratings ONLY -- never for stock status, buttons, checkout
 * discounts, success messages or navigation.
 */

/**
 * The pill overlaid on a product image ("★ 4.8").
 *
 * Renders nothing unless there is a real rating, so a product with no reviews
 * never shows an empty green pill or a "★ 0.0". Positioned bottom-left by CSS,
 * diagonally opposite the wishlist control, so the two can't collide.
 */
export function RatingPill({ rating, className = '', size = 11 }) {
  const value = Number(rating);
  if (!Number.isFinite(value) || value <= 0) return null;
  return (
    <span className={`product-rating-pill ${className}`.trim()}>
      <IconStar size={size} /> {value.toFixed(1)}
    </span>
  );
}

/**
 * The star row shown under a product name: green filled stars, navy-at-15%
 * empty stars, and a muted review count. Falls back to a plain "No reviews"
 * line when the product has no rating -- no zero-score, no row of empty stars.
 */
export function RatingStars({ rating, count, size = 12, className = '' }) {
  const { t } = useTranslation();
  const value = Number(rating);
  const hasRating = Number.isFinite(value) && value > 0;

  if (!hasRating) {
    return <span className={`product-no-reviews ${className}`.trim()}>{t('product.noReviews')}</span>;
  }

  const filled = Math.round(value);
  return (
    <>
      <span className="pcard-stars" aria-label={t('product.ratingOutOfFive', { rating: value.toFixed(1) })}>
        {Array.from({ length: 5 }, (_, i) => (
          <IconStar
            key={i}
            size={size}
            className={i < filled ? 'rating-star-filled' : 'rating-star-empty'}
            aria-hidden="true"
          />
        ))}
      </span>
      <span className="product-review-count">({count || 0})</span>
    </>
  );
}
